import type { HTMLAttributes } from 'react';
import { cn } from './cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** `flat` for dense lists, `raised` for standalone panels. */
  elevation?: 'flat' | 'raised';
  /** Adds the hover lift. Only for cards that are themselves a link or button. */
  interactive?: boolean;
  /**
   * A colour rule along one edge — pass an accent's `rule` class. The cheapest
   * way to tell a grid of cards apart without giving each one a different shape.
   */
  accentRule?: string;
  accentEdge?: 'top' | 'left';
}

export const Card = ({
  elevation = 'flat',
  interactive = false,
  accentRule,
  accentEdge = 'top',
  className,
  children,
  ...props
}: CardProps) => (
  <div
    className={cn(
      'relative bg-background-card border border-border rounded-lg overflow-hidden',
      elevation === 'raised' && 'shadow-sm',
      interactive &&
        'transition-all duration-300 ease-editorial hover:shadow-md hover:-translate-y-0.5 hover:border-primary/30 cursor-pointer',
      className,
    )}
    {...props}
  >
    {accentRule && (
      <span
        aria-hidden="true"
        className={cn(
          'absolute z-10',
          accentEdge === 'top' ? 'inset-x-0 top-0 h-1' : 'inset-y-0 left-0 w-1',
          accentRule,
        )}
      />
    )}
    {children}
  </div>
);

export const CardBody = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('p-5 sm:p-6', className)} {...props} />
);

export const CardHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-5 sm:px-6 pt-5 sm:pt-6 pb-4', className)} {...props} />
);

export const CardFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-5 sm:px-6 py-4 border-t border-border', className)} {...props} />
);

export default Card;
